import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useStore } from '@/store/useStore';

const categories = [
  { name: 'Footwear', path: '/category/footwear' },
  { name: 'Outerwear', path: '/category/outerwear' },
  { name: 'Backpacks', path: '/category/backpacks' },
  { name: 'Shelter & Sleep', path: '/category/shelter' },
  { name: 'Climbing', path: '/category/climbing' },
  { name: 'Navigation', path: '/category/navigation' },
];

export default function Header() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCategories, setShowCategories] = useState(false);
  const cartCount = useStore(state => state.cartCount());

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  return (
    <header className="bg-forest text-warm sticky top-0 z-50 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <svg className="w-8 h-8 text-amber" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 12h3v8h14v-8h3L12 2z" />
              <path d="M9 16h6v4H9z" />
            </svg>
            <span className="text-2xl font-serif font-bold tracking-tight">PeakPath</span>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <div
              className="relative"
              onMouseEnter={() => setShowCategories(true)}
              onMouseLeave={() => setShowCategories(false)}
            >
              <button className="text-warm-dark hover:text-warm transition-colors font-medium py-2">
                Shop
              </button>
              {showCategories && (
                <div className="absolute top-full left-0 w-48 bg-white rounded-lg shadow-xl py-2 border border-gray-200">
                  {categories.map(cat => (
                    <Link
                      key={cat.path}
                      to={cat.path}
                      className="block px-4 py-2 text-slate hover:bg-warm transition-colors"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link to="/category/new" className="text-warm-dark hover:text-warm transition-colors font-medium">
              New Arrivals
            </Link>
            <Link to="/category/sale" className="text-warm-dark hover:text-warm transition-colors font-medium">
              Sale
            </Link>
          </nav>

          {/* Search */}
          <form onSubmit={handleSearch} className="hidden lg:flex flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <input
                type="search"
                placeholder="Search gear..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pl-10 bg-forest-light text-warm placeholder-warm-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-amber"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-dark"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </form>

          {/* Icons */}
          <div className="flex items-center space-x-6">
            <Link to="/account" className="text-warm-dark hover:text-warm transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </Link>

            <Link to="/cart" className="text-warm-dark hover:text-warm transition-colors relative">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-amber text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>

      {/* Tagline bar */}
      <div className="bg-forest-dark text-warm-dark text-center py-1 text-sm">
        Gear for the journey ahead
      </div>
    </header>
  );
}
